import { exec } from "child_process";
import fs from "fs";
import path from "path";
import os from "os";
import { runCommand } from "./exec.js";

export interface SigningIdentity {
  hash: string;
  name: string;
  teamId: string;
  type: "development" | "distribution";
}

export interface ProvisioningProfile {
  uuid: string;
  name: string;
  teamId: string;
  appId: string; // e.g. "A1B2C3D4E5.com.example.app"
  bundleId: string; // e.g. "com.example.app"
  filePath: string;
}

/**
 * Scan macOS Keychain for valid code signing identities.
 */
export async function findIosIdentities(): Promise<SigningIdentity[]> {
  if (os.platform() !== "darwin") return [];

  const cmdResult = await runCommand("security find-identity -p codesigning -v");
  if (cmdResult.error) return [];

  const identities: SigningIdentity[] = [];
  const lines = cmdResult.stdout.split("\n");

  for (const line of lines) {
    // Match: 1) A1B2C3D4E5F6G7H8I9J0K1L2M3N4O5P6Q7R8S9T0 "Apple Development: Sanjay Kumar (A1B2C3D4E5)"
    const match = line.match(/^\s*\d+\)\s+([A-Z0-9]{40})\s+"(Apple (Development|Distribution|Developer|iPhone Developer|iPhone Distribution):.*?\(([A-Z0-9]{10})\))"/i);
    if (match && match[1] && match[2] && match[4]) {
      const hash = match[1];
      const name = match[2];
      const teamId = match[4];
      const type = name.toLowerCase().includes("distribution") ? "distribution" : "development";
      identities.push({ hash, name, teamId, type });
    }
  }

  return identities;
}

/**
 * Scan local MobileDevice provisioning profiles and extract metadata.
 */
export function findProvisioningProfiles(): ProvisioningProfile[] {
  if (os.platform() !== "darwin") return [];

  const profilesDir = path.join(os.homedir(), "Library", "MobileDevice", "Provisioning Profiles");
  if (!fs.existsSync(profilesDir)) return [];

  const profiles: ProvisioningProfile[] = [];
  try {
    const files = fs.readdirSync(profilesDir);
    for (const file of files) {
      if (file.endsWith(".mobileprovision") || file.endsWith(".provisionprofile")) {
        const filePath = path.join(profilesDir, file);
        try {
          const content = fs.readFileSync(filePath, "ascii");
          const xmlStart = content.indexOf("<?xml");
          const xmlEnd = content.indexOf("</plist>");
          
          if (xmlStart !== -1 && xmlEnd !== -1) {
            const xml = content.slice(xmlStart, xmlEnd + "</plist>".length);
            
            const uuidMatch = xml.match(/<key>UUID<\/key>\s*<string>([^<]+)<\/string>/);
            const nameMatch = xml.match(/<key>Name<\/key>\s*<string>([^<]+)<\/string>/);
            
            // TeamIdentifier is inside <key>TeamIdentifier</key>\s*<array>\s*<string>([^<]+)</string>
            const teamMatch = xml.match(/<key>TeamIdentifier<\/key>\s*<array>\s*<string>([^<]+)<\/string>/);
            
            // App ID prefix / Entitlements application-identifier
            const appIdMatch = xml.match(/<key>application-identifier<\/key>\s*<string>([^<]+)<\/string>/);
            
            if (uuidMatch && nameMatch) {
              const uuid = uuidMatch[1]!.trim();
              const name = nameMatch[1]!.trim();
              const teamId = teamMatch ? teamMatch[1]!.trim() : "";
              const appId = appIdMatch ? appIdMatch[1]!.trim() : "";
              
              // Extract raw bundle ID (remove team prefix, e.g. "A1B2C3D4E5.com.example" -> "com.example")
              let bundleId = appId;
              if (teamId && appId.startsWith(teamId + ".")) {
                bundleId = appId.slice(teamId.length + 1);
              }

              profiles.push({
                uuid,
                name,
                teamId,
                appId,
                bundleId,
                filePath
              });
            }
          }
        } catch {
          // Skip unreadable files
        }
      }
    }
  } catch {
    // Skip on folder read errors
  }

  return profiles;
}
